import React, { useState, useEffect } from "react";
import { MessageSquare, X, Loader } from "lucide-react";
import { supabase } from "../lib/supabase";

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (formData: ContactFormData) => Promise<boolean>;
  className?: string;
}

export default function ContactModal({
  isOpen,
  onClose,
  onSubmit,
  className = "",
}: ContactModalProps) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    if (!validateForm()) {
      e.preventDefault();
      return;
    }

    if (onSubmit) {
      // Use the provided onSubmit function if available (which may include database operations)
      e.preventDefault();
      setIsSubmitting(true);
      setErrors({});

      console.log("Contact form submission started");

      try {
        const formDataObj = {
          name: `${formData.first_name} ${formData.last_name}`.trim(),
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
        };
        console.log("Submitting contact form with data:", formDataObj);
        const success = await onSubmit(formDataObj);

        if (success) {
          setSubmitSuccess(true);
          setTimeout(() => {
            onClose();
            setSubmitSuccess(false);
            setFormData({
              first_name: "",
              last_name: "",
              email: "",
              phone: "",
              subject: "General Inquiry",
              message: "",
            });
          }, 2000);
        } else {
          setErrors({
            submit: "Failed to send message. Please try again later.",
          });
        }
      } catch (error) {
        console.error("Error submitting contact form:", error);
        setErrors({
          submit: "Failed to send message. Please try again later.",
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Use Netlify Forms - submit the form data programmatically
      e.preventDefault();
      setIsSubmitting(true);
      setErrors({});

      console.log("Form submitted via Netlify Forms");

      try {
        // Create FormData object with all the form fields
        const formDataToSubmit = new FormData();
        formDataToSubmit.append("form-name", "contact");
        formDataToSubmit.append("first_name", formData.first_name);
        formDataToSubmit.append("last_name", formData.last_name);
        formDataToSubmit.append("email", formData.email);
        formDataToSubmit.append("phone", formData.phone);
        formDataToSubmit.append("subject", formData.subject);
        formDataToSubmit.append("message", formData.message);

        // Submit to Netlify Forms
        const response = await fetch("/", {
          method: "POST",
          body: formDataToSubmit,
        });

        if (response.ok) {
          setSubmitSuccess(true);
          setTimeout(() => {
            onClose();
            setSubmitSuccess(false);
            setFormData({
              first_name: "",
              last_name: "",
              email: "",
              phone: "",
              subject: "General Inquiry",
              message: "",
            });
          }, 2000);
        } else {
          setErrors({
            submit: "Failed to send message. Please try again later.",
          });
        }
      } catch (error) {
        console.error("Error submitting contact form:", error);
        setErrors({
          submit: "Failed to send message. Please try again later.",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto ${className}`}
      onClick={handleOverlayClick}
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MessageSquare className="h-6 w-6 text-[#F98B3D] mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                Contact Us
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Message Sent!
              </h3>
              <p className="text-gray-600">We'll get back to you soon.</p>
            </div>
          ) : (
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* Netlify form fields */}
              <input type="hidden" name="form-name" value="contact" />
              <div hidden>
                <input name="bot-field" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="first_name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    className={`mt-1 block w-full rounded-md shadow-sm ${
                      errors.first_name ? "border-red-300" : "border-gray-300"
                    } focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50`}
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.first_name}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="last_name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    className={`mt-1 block w-full rounded-md shadow-sm ${
                      errors.last_name ? "border-red-300" : "border-gray-300"
                    } focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50`}
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.last_name}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.email ? "border-red-300" : "border-gray-300"
                  } focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                />
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700"
                >
                  Subject
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                >
                  <option>General Inquiry</option>
                  <option>Training Information</option>
                  <option>Fractional PM Services</option>
                  <option>AI Inquiry</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    errors.message ? "border-red-300" : "border-gray-300"
                  } focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50`}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                )}
              </div>

              {errors.submit && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                  <p className="text-sm text-red-700">{errors.submit}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D] ${
                  isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                }`}
                onClick={() => console.log("Submit button clicked")}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
